/**
 * Unified Resident App Design System Colors
 * 
 * This file consolidates all color tokens for the resident app (mobile & desktop).
 * All feature-specific color files should reference these tokens for consistency.
 * 
 * Structure:
 * 1. PRIMITIVES - Raw color values (the actual hex codes)
 * 2. SEMANTIC - Purpose-based tokens (what colors mean)
 * 3. COMPONENT - UI element-specific tokens (how colors are applied)
 */

// =============================================================================
// PRIMITIVES - Raw Color Values
// =============================================================================

export const PRIMITIVE_COLORS = {
  // Grayscale
  black: '#000000',
  white: '#FFFFFF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#C8C8C8',
  gray300: '#ACACAC',
  gray400: '#919191',
  gray500: '#757575',
  gray600: '#5E5E5E',
  gray700: '#464646',
  gray800: '#3F3F3F',
  gray850: '#2F2F2F',
  gray900: '#242424',
  gray950: '#171717',

  // Brand Blues
  resioBlue: '#4672FE',
  resioBlueBorder: '#4773FE',
  resioBlueLight: '#6B8EFE',
  resioBlueBg: 'rgba(70, 114, 254, 0.15)',
  resioBlueBgLight: 'rgba(71, 114, 254, 0.13)',
  resioBlueAttention: '#073EEC',

  // Accent Pinks/Reds
  resioPink: '#F55560',
  resioRed: '#EF4444',
  resioDangerRed: '#FF3333',
  resioGradientRed: '#EF303D',

  // Accent Purple
  resioPurple: '#BE5EC8',
  resioPurpleLight: '#CB7ED3',

  // Success/Warning/Info
  successGreen: '#66CE66',
  successGreenBright: '#10B981',
  successGreenLight: '#D1FAE5',
  warningOrange: '#FF7629',
  warningYellow: '#F59E0B',
  starYellow: '#FFD700',
  infoTeal: '#009688',

  // Transparency Variants
  white5: 'rgba(238, 241, 244, 0.05)',
  white10: 'rgba(238, 241, 244, 0.1)',
  white25: 'rgba(255, 255, 255, 0.25)',
  white50: 'rgba(238, 241, 244, 0.5)',
  white70: 'rgba(238, 241, 244, 0.7)',
  white80: 'rgba(238, 241, 244, 0.8)',
  black55: 'rgba(0, 0, 0, 0.55)',
  blackHover: '#242424cc',
  purpleBg50: 'rgba(23, 23, 23, 0.5)',
  greenBg10: 'rgba(102, 206, 102, 0.1)',
} as const;

// =============================================================================
// SEMANTIC TOKENS - Purpose-Based Colors
// =============================================================================

export const SEMANTIC_COLORS = {
  // Backgrounds
  background: {
    primary: PRIMITIVE_COLORS.gray950,       // Main app background
    secondary: PRIMITIVE_COLORS.gray900,     // Cards, modals
    tertiary: PRIMITIVE_COLORS.gray850,      // Inputs, toggles, avatars
    input: PRIMITIVE_COLORS.gray700,         // Form inputs
    overlay: PRIMITIVE_COLORS.black55,       // Overlays, badges
    hover: PRIMITIVE_COLORS.blackHover,      // Hover states
  },

  // Text
  text: {
    primary: '#EEF1F4',                      // Main text (nearly white)
    secondary: PRIMITIVE_COLORS.gray300,     // Muted text
    tertiary: PRIMITIVE_COLORS.gray400,      // Very muted text
    muted: PRIMITIVE_COLORS.gray500,         // Disabled/placeholder
    light: '#E3E3E3',                        // Light text on dark
    inverse: PRIMITIVE_COLORS.gray950,       // Dark text on light bg
    link: PRIMITIVE_COLORS.resioBlueLight,   // Link text
  },

  // Borders
  border: {
    primary: PRIMITIVE_COLORS.resioBlueBorder,  // Accent border
    secondary: PRIMITIVE_COLORS.gray700,        // Dividers, subtle borders
    tertiary: PRIMITIVE_COLORS.gray800,         // Very subtle borders
    input: PRIMITIVE_COLORS.gray500,            // Input borders
    focus: PRIMITIVE_COLORS.resioBlue,          // Focus state
  },

  // Interactive Elements
  interactive: {
    primary: PRIMITIVE_COLORS.resioBlue,        // Primary buttons, links
    primaryHover: PRIMITIVE_COLORS.resioBlueLight,
    secondary: PRIMITIVE_COLORS.resioPink,      // Secondary actions
    danger: PRIMITIVE_COLORS.resioDangerRed,    // Destructive actions
    disabled: PRIMITIVE_COLORS.gray600,         // Disabled state
  },

  // Status Colors
  status: {
    success: PRIMITIVE_COLORS.successGreen,
    successBg: PRIMITIVE_COLORS.greenBg10,
    warning: PRIMITIVE_COLORS.warningOrange,
    error: PRIMITIVE_COLORS.resioRed,
    info: PRIMITIVE_COLORS.resioBlue,
  },

  // Icons
  icon: {
    primary: '#EEF1F4',                      // Primary icons (light)
    secondary: PRIMITIVE_COLORS.gray400,     // Muted icons
    muted: PRIMITIVE_COLORS.gray500,         // Very muted icons
    inactive: '#9CA3AF',                     // Inactive nav icons
    inverse: PRIMITIVE_COLORS.gray950,       // Dark icons on light bg
  },
} as const;

// =============================================================================
// COMPONENT TOKENS - UI Element-Specific Colors
// =============================================================================

export const COMPONENT_COLORS = {
  // Navigation
  nav: {
    background: PRIMITIVE_COLORS.gray900,
    activeBackground: PRIMITIVE_COLORS.gray850,
    activeBorder: PRIMITIVE_COLORS.resioBlueLight,
    inactiveBorder: PRIMITIVE_COLORS.gray850,
    headerBlue: PRIMITIVE_COLORS.resioBlue,
    headerPurple: PRIMITIVE_COLORS.resioPurple,
  },

  // Buttons
  button: {
    primaryBg: PRIMITIVE_COLORS.resioBlue,
    primaryText: '#EEF1F4',
    secondaryBg: 'transparent',
    secondaryBorder: PRIMITIVE_COLORS.resioBlue,
    secondaryText: PRIMITIVE_COLORS.resioBlueLight,
    dangerBg: PRIMITIVE_COLORS.resioPink,
    dangerText: PRIMITIVE_COLORS.gray950,
    outlineBg: 'transparent',
    outlineBorder: '#EEF1F4',
    outlineText: '#EEF1F4',
    disabledBg: PRIMITIVE_COLORS.gray600,
    disabledText: '#E3E3E3',
  },

  // Cards
  card: {
    background: PRIMITIVE_COLORS.gray900,
    border: PRIMITIVE_COLORS.gray700,
    readBackground: 'transparent',
    readBorder: PRIMITIVE_COLORS.gray850,
  },

  // Inputs & Forms
  input: {
    background: PRIMITIVE_COLORS.gray700,
    border: PRIMITIVE_COLORS.gray500,
    focusBorder: PRIMITIVE_COLORS.resioBlue,
    placeholder: PRIMITIVE_COLORS.gray300,
    text: '#EEF1F4',
  },

  // Toggles & Switches
  toggle: {
    background: PRIMITIVE_COLORS.gray850,
    activeBackground: PRIMITIVE_COLORS.gray700,
    trackOff: PRIMITIVE_COLORS.gray700,
    trackOn: PRIMITIVE_COLORS.resioBlue,
    thumb: '#EEF1F4',
  },

  // Avatar
  avatar: {
    background: PRIMITIVE_COLORS.white10,
    text: '#EEF1F4',
    placeholder: PRIMITIVE_COLORS.gray850,
  },

  // Chat/Messages
  chat: {
    sentBubble: PRIMITIVE_COLORS.resioBlue,
    receivedBubble: PRIMITIVE_COLORS.gray850,
    inputBackground: PRIMITIVE_COLORS.white5,
    timestamp: PRIMITIVE_COLORS.white70,
  },

  // Notifications
  notification: {
    unreadDot: PRIMITIVE_COLORS.warningOrange,
    unreadBackground: PRIMITIVE_COLORS.white5,
  },

  // Checkboxes & Radio
  checkbox: {
    background: PRIMITIVE_COLORS.gray700,
    border: PRIMITIVE_COLORS.gray500,
    selected: PRIMITIVE_COLORS.resioPink,
    checkmark: '#EEF1F4',
  },

  // Carousel
  carousel: {
    dotActive: '#EEF1F4',
    dotInactive: PRIMITIVE_COLORS.gray500,
    background: PRIMITIVE_COLORS.black55,
  },

  // Badges
  badge: {
    priceBg: PRIMITIVE_COLORS.black55,
    attendingBg: PRIMITIVE_COLORS.greenBg10,
    attendingText: PRIMITIVE_COLORS.successGreen,
    purpleBg: PRIMITIVE_COLORS.purpleBg50,
    purpleText: PRIMITIVE_COLORS.resioPurpleLight,
  },

  // Gradients (for reference - use with LinearGradient)
  gradient: {
    orangeadeStart: '#EF303D',
    orangeadeEnd: '#073EEC',
  },
} as const;

// =============================================================================
// LEGACY SUPPORT - Mobile Theme (Light Theme for some screens)
// =============================================================================

export const MOBILE_LIGHT_COLORS = {
  background: '#F2F2F7',
  textPrimary: '#000000',
  textSecondary: '#FFFFFF',
  textTertiary: '#8E8E93',
  buttonPrimary: '#4773FE',
  buttonSecondary: '#ff1100',
  borderPrimary: '#C7C7CC',
  avatarBackground: '#E5E5EA',
} as const;

// =============================================================================
// EXPORTS - Unified Access
// =============================================================================

/**
 * Complete resident design system colors
 * Use this as the single source of truth for all colors
 */
export const RESIDENT_COLORS = {
  primitives: PRIMITIVE_COLORS,
  semantic: SEMANTIC_COLORS,
  components: COMPONENT_COLORS,
  mobileLight: MOBILE_LIGHT_COLORS,
} as const;

// Type exports
export type PrimitiveColor = keyof typeof PRIMITIVE_COLORS;
export type SemanticColor = typeof SEMANTIC_COLORS;
export type ComponentColor = typeof COMPONENT_COLORS;
